import {
    DISPLAY_TOKEN_GLOSSARY_I18N_KEY,
    formatDisplayLabel as formatDisplayLabelWithGlossary,
    parseDisplayTokenGlossary,
} from 'Core-utils/i18n/displayLabel';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAuthenticatedContentfulEntriesCached, fetchPublicContentfulEntriesCached } from 'Apis/contentfulProxy';
import { useSelector } from 'react-redux';
import { RootState } from 'Rdx/store';

const VIDEO_PLAYER_PUBLIC_I18N_KEYS = [
    'COMPLIANCE_TRAINING_MISSING_TRAINING_ID_TITLE',
    'COMPLIANCE_TRAINING_MISSING_TRAINING_ID_DETAIL',
    'COMPLIANCE_TRAINING_LOADING_VIDEO',
    'COMPLIANCE_TRAINING_VIDEO_PLAYER_NO_JS_MESSAGE',
    'COMPLIANCE_TRAINING_VIDEO_PLAYER_COMPLETION_MESSAGE_TITLE',
];

type I18nMap = Record<string, string>;

export const useI18n = () => {
    const token = useSelector((state: RootState) => state.auth.token);
    const [translations, setTranslations] = useState<I18nMap>({});
    const [loading, setLoading] = useState(false);
    const load = useCallback(
        async (preferCache = true) => {
            try {
                setLoading(true);
                const response = token
                    ? await fetchAuthenticatedContentfulEntriesCached(
                          { ['content_type']: 'securityHubI18n', limit: 1000 },
                          token,
                          { preferCache, ttlMs: 60 * 60 * 1000 },
                      )
                    : await fetchPublicContentfulEntriesCached(
                          {
                              ['content_type']: 'securityHubI18n',
                              'fields.key[in]': VIDEO_PLAYER_PUBLIC_I18N_KEYS.join(','),
                              limit: VIDEO_PLAYER_PUBLIC_I18N_KEYS.length,
                          },
                          { preferCache, ttlMs: 60 * 60 * 1000 },
                      );

                const mappedTranslations = (response?.items ?? []).reduce((acc: I18nMap, item: any) => {
                    const key = item?.fields?.key;
                    const value = item?.fields?.value;
                    if (typeof key === 'string' && typeof value === 'string') {
                        acc[key] = value;
                    }
                    return acc;
                }, {});
                setTranslations(mappedTranslations);
            } finally {
                setLoading(false);
            }
        },
        [token],
    );

    useEffect(() => {
        void load();
    }, [load]);

    const refresh = useCallback(async () => {
        await load(false);
    }, [load]);

    const t = useCallback(
        (key: string, defaultValue?: string) => translations[key] ?? defaultValue ?? key,
        [translations],
    );
    const displayTokenGlossaryRaw = t(DISPLAY_TOKEN_GLOSSARY_I18N_KEY, '{}');
    const displayTokenGlossary = useMemo(
        () => parseDisplayTokenGlossary(displayTokenGlossaryRaw),
        [displayTokenGlossaryRaw],
    );

    const formatDisplayLabel = useCallback(
        (value: string | null | undefined) => formatDisplayLabelWithGlossary(value, displayTokenGlossary),
        [displayTokenGlossary],
    );

    return {
        t,
        formatDisplayLabel,
        loading,
        refresh,
    };
};
